import React from 'react';
import { eventCreationDateErrors } from '../../domain/events/creationDates';
import { useNavigate } from 'react-router-dom';
import type { ParsedBEOPacket, ParsedBEOEvent, ParsedBEORoom, ParsedBEOContact, ParsedBEOLineItem, ParsedBEONumberEntry } from '../../hooks/useBEOScan';
import { useToasts } from '../../ui/ToastProvider';
import useActiveLocationStore from '../../state/locationScope';
import { getSupabaseClient } from '../../data/supabase';
import {
  fetchSiteOptions,
  searchVenues,
  fetchVenueRooms,
  fetchNextOrderRefForSite,
  consumeOrderRefHold,
  releaseOrderRefHold,
  createEventBundle,
  resolveLocationForVenue,
  type SiteOption,
  type VenueOption,
  type VenueRoomTemplate,
  type RoomDraft,
} from '../../data/eventWizard';
import { createEventContact, attachClientToEvent } from '../../data/eventContacts';
import { upsertEventLine } from '../../data/eventLines';
import { searchProductItemLinks } from '../../data/items';
import { fetchEventDays, updateEventDay } from '../../data/eventDays';
import { fetchClientSearchResults, createClient, insertClientContact, fetchClientBranchDetails, insertClientBranch, updateClientBranch } from '../../data/clients';
import { safeErrorMessage, toPartialSaveError } from '../../utils/appErrors';

/* ── helpers ─────────────────────────────────────────────────────────────── */

type OrderNumberHold = { code: string; locationId: string };

/** Mark a claimed order number's hold as used, or release it. Releasing a number an event
 *  already uses marks it used instead, so a partly created event keeps its number. */
async function settleOrderNumber(hold: OrderNumberHold, used: boolean): Promise<void> {
  try {
    await (used ? consumeOrderRefHold(hold) : releaseOrderRefHold(hold));
  } catch (err) {
    console.warn(`[BEOImportReview] could not ${used ? 'mark as used' : 'release'} order number ${hold.code}`, err);
  }
}

function splitName(full: string | null | undefined): { first: string; last: string } {
  if (!full) return { first: '', last: '' };
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

function mapContactRole(beoRole: string | null | undefined): string {
  if (!beoRole) return 'event_contact';
  const lower = beoRole.toLowerCase();
  if (/venue|hotel|catering|banquet|chef|food|beverage/.test(lower)) return 'venue';
  if (/planner|coordinator|client|organizer|customer|billing/.test(lower)) return 'client';
  return 'event_contact';
}

/** Normalize a business name for fuzzy comparison: lowercase, strip punctuation, collapse whitespace. */
function normalizeName(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

/** Format YYYY-MM-DD to compact display like "Mar 03" */
function fmtDate(d: string | null | undefined): string {
  if (!d) return '';
  const [, m, day] = d.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1] || m} ${day}`;
}

/** Format date range for collapsed header */
function fmtDateRange(start: string, end: string): string {
  if (!start && !end) return '';
  if (!end || start === end) return fmtDate(start);
  // Same month? Compress: "Mar 03-07"
  if (start.slice(0, 7) === end.slice(0, 7)) return `${fmtDate(start)}-${end.split('-')[2]}`;
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}

function buildNotesText(ev: ParsedBEOEvent): string {
  const sections: string[] = [];
  if (ev.av_requirements) sections.push(`AV Requirements:\n${ev.av_requirements}`);
  if (ev.catering_summary) sections.push(`Catering:\n${ev.catering_summary}`);
  if (ev.special_requirements) sections.push(`Special Requirements:\n${ev.special_requirements}`);
  return sections.join('\n\n');
}

/** Detect generic summary line items that shouldn't be imported (e.g. "Audio Visual Equipment Rentals") */
function isGenericSummaryLine(desc: string): boolean {
  const d = desc.toLowerCase().trim();
  return /^audio\s*visual\s*(equipment\s*)?(rentals?|charges?|services?)?$/i.test(d);
}

/* ── per-event state types ──────────────────────────────────────────────── */

interface RoomSelection extends ParsedBEORoom { selected: boolean; templateId?: string | null; isNew: boolean }
interface ContactSelection extends ParsedBEOContact { selected: boolean }
interface LineItemSelection extends ParsedBEOLineItem {
  selected: boolean;
  /** User override: product chosen via inline search */
  override_product_id?: string | null;
  override_product_name?: string | null;
  override_item_id?: string | null;
}

interface ClientMatch { id: string; businessName: string }

interface EventState {
  selected: boolean;
  expanded: boolean;
  eventName: string;
  beoNumbers: { date: string; beoNo: string }[];
  clientName: string;
  clientMatch: ClientMatch | null;
  clientIsNew: boolean;
  clientSearchOpen: boolean;
  clientSearchQuery: string;
  clientSearchResults: ClientMatch[];
  clientSearchLoading: boolean;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  attendees: string;
  existingEventId: string | null;
  existingEventName: string | null;
  rooms: RoomSelection[];
  contacts: ContactSelection[];
  lineItems: LineItemSelection[];
  notes: string;
  venueName: string;
  venueId: string | null;
  siteLocationId: string | null;
  venueMatches: VenueOption[];
  createNewVenue: boolean;
  locationAutoResolved: boolean;
}

/* ── props ──────────────────────────────────────────────────────────────── */

interface Props {
  packet: ParsedBEOPacket;
  onCancel: () => void;
  onComplete?: () => void;
}

/* ── component ──────────────────────────────────────────────────────────── */

export default function BEOImportReview({ packet, onCancel, onComplete }: Props) {
  const navigate = useNavigate();
  const toasts = useToasts();
  const activeLocationId = useActiveLocationStore((s) => s.activeLocationId);

  /* ── shared state ────────────────────────────────────────────── */
  const [sites, setSites] = React.useState<SiteOption[]>([]);
  const [creating, setCreating] = React.useState(false);
  // A second Create before the first re-renders would claim a second set of order numbers.
  const creatingRef = React.useRef(false);
  const [step, setStep] = React.useState<'summary' | 'review'>(() => {
    const set = new Set<string>();
    packet.events.forEach((ev) => {
      const name = (ev.venue_name || packet.venue_name || '').trim().toLowerCase();
      if (name) set.add(name);
    });
    return set.size > 1 ? 'summary' : 'review';
  });

  /* ── per-event state ─────────────────────────────────────────── */
  const [events, setEvents] = React.useState<EventState[]>(() =>
    packet.events.map((ev, i) => ({
      selected: true,
      expanded: i === 0,
      eventName: ev.event_name || '',
      beoNumbers: (ev.beo_numbers || []).map((b) => ({ date: b.date, beoNo: b.beo_no })),
      clientName: ev.client_name || '',
      clientMatch: null,
      clientIsNew: false,
      clientSearchOpen: false,
      clientSearchQuery: '',
      clientSearchResults: [],
      clientSearchLoading: false,
      startDate: ev.event_start_date || '',
      endDate: ev.event_end_date || '',
      startTime: ev.event_start_time || '',
      endTime: ev.event_end_time || '',
      attendees: String(ev.anticipated_attendees ?? ev.guaranteed_count ?? ''),
      existingEventId: null,
      existingEventName: null,
      rooms: (ev.rooms || []).map((r) => ({ ...r, selected: true, templateId: null, isNew: true })),
      contacts: (ev.contacts || []).map((c) => ({ ...c, selected: true })),
      lineItems: (ev.line_items || [])
        .filter((li) => !isGenericSummaryLine(li.description))
        .map((li) => ({ ...li, selected: !li.is_generic })),
      notes: buildNotesText(ev),
      venueName: ev.venue_name || packet.venue_name || '',
      venueId: null,
      siteLocationId: activeLocationId ?? null,
      venueMatches: [],
      createNewVenue: false,
      locationAutoResolved: false,
    })),
  );

  function updateEvent(idx: number, patch: Partial<EventState>) {
    setEvents((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  }

  /** Derive multi-venue status from per-event venue names */
  const isMultiVenue = React.useMemo(() => {
    const set = new Set<string>();
    events.forEach((ev) => {
      const name = (ev.venueName || '').trim().toLowerCase();
      if (name) set.add(name);
    });
    return set.size > 1;
  }, [events]);

  /* ── load site options on mount ─────────────────────────────── */
  React.useEffect(() => {
    (async () => {
      const { data: opts } = await fetchSiteOptions(null);
      setSites(opts || []);
      // If only one location and events have no location yet, auto-assign
      if (opts?.length === 1) {
        const loc = opts[0].location_id;
        setEvents((prev) => prev.map((e) => (!e.siteLocationId ? { ...e, siteLocationId: loc } : e)));
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── per-event venue search (grouped by unique name) ─────── */
  React.useEffect(() => {
    let cancelled = false;
    const groups = new Map<string, number[]>();
    events.forEach((ev, i) => {
      const key = (ev.venueName || '').trim().toLowerCase();
      if (!key) return;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(i);
    });
    for (const [, indices] of groups) {
      const vName = events[indices[0]].venueName;
      searchVenues(vName).then(({ data }) => {
        if (cancelled) return;
        const results = data || [];
        const autoId = results.length === 1 ? results[0].id : null;
        setEvents((prev) =>
          prev.map((e, i) =>
            indices.includes(i)
              ? { ...e, venueMatches: results, venueId: autoId ?? e.venueId, createNewVenue: results.length === 0 }
              : e,
          ),
        );
      });
    }
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── per-event location auto-resolve from venue match ──────── */
  const venueIdKey = events.map((e) => e.venueId || '').join(',');
  React.useEffect(() => {
    let cancelled = false;
    events.forEach((ev, i) => {
      if (!ev.venueId || ev.locationAutoResolved) return;
      resolveLocationForVenue(ev.venueId).then(({ locationId }) => {
        if (cancelled || !locationId) return;
        setEvents((prev) => prev.map((e, idx) =>
          idx === i ? { ...e, siteLocationId: locationId, locationAutoResolved: true } : e,
        ));
      });
    });
    return () => { cancelled = true; };
  }, [venueIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── fetch venue room templates per unique venueId ────────── */
  React.useEffect(() => {
    let cancelled = false;
    const venueIds = new Set<string>();
    events.forEach((ev) => { if (ev.venueId) venueIds.add(ev.venueId); });
    for (const vid of venueIds) {
      fetchVenueRooms(vid).then(({ data: templates }) => {
        if (cancelled || !templates?.length) return;
        setEvents((prev) =>
          prev.map((ev) =>
            ev.venueId === vid
              ? {
                  ...ev,
                  rooms: ev.rooms.map((r) => {
                    const match = templates.find((t) => t.name.toLowerCase() === r.name.toLowerCase());
                    return match ? { ...r, templateId: match.id, isNew: false } : { ...r, templateId: null, isNew: true };
                  }),
                }
              : ev,
          ),
        );
      });
    }
    return () => { cancelled = true; };
  }, [venueIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── client matching on mount ───────────────────────────────── */
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let i = 0; i < packet.events.length; i++) {
        const clientName = packet.events[i].client_name;
        if (!clientName || cancelled) continue;
        // Try full name first, then without common suffixes (Inc., LLC, Corp, etc.)
        let results: Awaited<ReturnType<typeof fetchClientSearchResults>>['data'] = [];
        const { data: fullResults } = await fetchClientSearchResults(clientName);
        if (cancelled) break;
        results = fullResults || [];
        // If no results, retry with stripped suffixes (e.g. "Miramar Services, Inc." → "Miramar Services")
        if (!results.length) {
          const stripped = clientName.replace(/,?\s*(Inc\.?|LLC\.?|Corp\.?|Co\.?|Ltd\.?|LP|LLP|PLC|SA|GmbH)\s*$/i, '').trim();
          if (stripped && stripped !== clientName) {
            const { data: retryResults } = await fetchClientSearchResults(stripped);
            if (cancelled) break;
            results = retryResults || [];
          }
        }
        const normalizedClientName = normalizeName(clientName);
        const exact = results.find((c) => normalizeName(c.businessName) === normalizedClientName);
        // Also check if a result's normalized name is contained in (or contains) the parsed name
        const fuzzy = !exact ? results.find((c) => {
          const n = normalizeName(c.businessName);
          return n.includes(normalizedClientName) || normalizedClientName.includes(n);
        }) : null;
        const best = exact || fuzzy || (results.length ? results[0] : null);
        setEvents((prev) =>
          prev.map((e, idx) =>
            idx === i
              ? {
                  ...e,
                  clientMatch: best ? { id: best.id, businessName: best.businessName } : null,
                  clientIsNew: !best,
                }
              : e,
          ),
        );
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── per-event client search (debounced) ──────────────────── */
  const clientSearchTimers = React.useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  function handleClientSearchChange(evIdx: number, query: string) {
    updateEvent(evIdx, { clientSearchQuery: query, clientSearchLoading: true });
    // Clear previous timer for this event
    if (clientSearchTimers.current[evIdx]) clearTimeout(clientSearchTimers.current[evIdx]);
    if (!query.trim()) {
      updateEvent(evIdx, { clientSearchResults: [], clientSearchLoading: false });
      return;
    }
    clientSearchTimers.current[evIdx] = setTimeout(async () => {
      const { data: results } = await fetchClientSearchResults(query);
      setEvents((prev) => prev.map((e, i) => i === evIdx ? { ...e, clientSearchResults: (results || []).map((r) => ({ id: r.id, businessName: r.businessName })), clientSearchLoading: false } : e));
    }, 300);
  }

  function selectClient(evIdx: number, match: ClientMatch) {
    updateEvent(evIdx, {
      clientMatch: match,
      clientName: match.businessName,
      clientIsNew: false,
      clientSearchOpen: false,
      clientSearchQuery: '',
      clientSearchResults: [],
    });
  }

  function clearClientMatch(evIdx: number) {
    updateEvent(evIdx, {
      clientMatch: null,
      clientIsNew: true,
      clientSearchOpen: true,
      clientSearchQuery: '',
      clientSearchResults: [],
    });
  }

  /* ── duplicate event detection by BEO # ──────────────────── */
  React.useEffect(() => {
    const supabase = getSupabaseClient();
    let cancelled = false;
    (async () => {
      for (let i = 0; i < packet.events.length; i++) {
        const beoNos = (packet.events[i].beo_numbers || []).map((b) => b.beo_no).filter(Boolean);
        if (!beoNos.length || cancelled) continue;
        // Check events.beo_no (backward compat) and event_days.beo_no
        const { data: evMatch } = await supabase
          .from('events')
          .select('id, name')
          .in('beo_no', beoNos)
          .limit(1)
          .maybeSingle();
        if (cancelled) break;
        if (evMatch) {
          setEvents((prev) => prev.map((e, idx) => idx === i ? { ...e, existingEventId: evMatch.id, existingEventName: evMatch.name, selected: false } : e));
          continue;
        }
        const { data: dayMatch } = await supabase
          .from('event_days')
          .select('event_id, beo_no')
          .in('beo_no', beoNos)
          .is('deleted_at', null)
          .limit(1)
          .maybeSingle();
        if (cancelled) break;
        if (dayMatch) {
          const { data: parentEvent } = await supabase
            .from('events')
            .select('id, name')
            .eq('id', dayMatch.event_id)
            .maybeSingle();
          if (cancelled) break;
          if (parentEvent) {
            setEvents((prev) => prev.map((e, idx) => idx === i ? { ...e, existingEventId: parentEvent.id, existingEventName: parentEvent.name, selected: false } : e));
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── create all selected events ─────────────────────────────── */
  async function handleCreate() {
    if (creatingRef.current) return;
    const selectedEvents = events.filter((e) => e.selected);
    if (!selectedEvents.length) { toasts.push({ type: 'error', message: 'No events selected' }); return; }

    for (let i = 0; i < selectedEvents.length; i++) {
      const ev = selectedEvents[i];
      if (!ev.eventName.trim()) { toasts.push({ type: 'error', message: `Event ${i + 1}: name is required` }); return; }
      const dateErrors = eventCreationDateErrors(ev.startDate, ev.endDate);
      if (dateErrors.start || dateErrors.end) { toasts.push({ type: 'error', message: `Event "${ev.eventName}": ${dateErrors.start || dateErrors.end}` }); return; }
      if (!ev.siteLocationId) { toasts.push({ type: 'error', message: `Event "${ev.eventName}": please select a location` }); return; }
      if (!ev.venueId && !ev.createNewVenue) { toasts.push({ type: 'error', message: `Event "${ev.eventName}": please select or create a venue` }); return; }
    }

    creatingRef.current = true;
    setCreating(true);
    const createdEventIds: string[] = [];
    const createdCodes: string[] = [];
    // The claimed number of the event being created, until it is marked used or given back.
    let pendingHold: OrderNumberHold | null = null;

    try {
      const supabase = getSupabaseClient();

      for (const ev of selectedEvents) {
        const parsedIdx = events.indexOf(ev);
        const parsedEvent = packet.events[parsedIdx];
        const siteId = ev.siteLocationId;
        if (!siteId) continue;

        // Claim this event's order number only now, so opening, editing or
        // cancelling the review never holds one.
        const code = await fetchNextOrderRefForSite(siteId).catch(() => null);
        if (!code) {
          toasts.push({ type: 'error', message: `Event "${ev.eventName}" was not created: no order number could be assigned for its location.` });
          continue;
        }
        pendingHold = { code, locationId: siteId };

        // 1. Resolve client
        let clientId: string | null = null;
        if (ev.clientMatch) {
          clientId = ev.clientMatch.id;
        } else if (ev.clientName.trim() && ev.clientIsNew) {
          const { data: newClient } = await createClient(ev.clientName.trim(), { supabase });
          if (newClient) clientId = newClient.id;
        }

        // 2. Build room drafts
        const selectedRooms: RoomDraft[] = ev.rooms
          .filter((r) => r.selected)
          .map((r) => ({ name: r.name, type: r.setup_style ?? null, capacity: r.capacity ?? null, templateId: r.templateId ?? null }));

        // 3. Create event bundle
        const eventPayload: any = {
          name: ev.eventName.trim(),
          event_code: code,
          show_start_date: ev.startDate || null,
          show_end_date: ev.endDate || null,
          event_start_date: ev.startDate || null,
          event_end_date: ev.endDate || null,
          site_location_id: ev.siteLocationId,
          // Created with its client, so the new event's Salesperson defaults to the client's salesperson.
          client_id: clientId,
        };

        const venueSelection = ev.venueId
          ? { venueId: ev.venueId }
          : ev.createNewVenue
            ? {
                createVenue: {
                  venue_name: ev.venueName || ev.eventName,
                  city: packet.venue_address?.city ?? null,
                  state: packet.venue_address?.state ?? null,
                },
              }
            : undefined;

        const { event: newEvent, error: bundleErr } = await createEventBundle({
          event: eventPayload,
          venueSelection,
          rooms: selectedRooms,
          supabase,
        });

        if (bundleErr || !newEvent) {
          await settleOrderNumber(pendingHold, false);
          pendingHold = null;
          toasts.push({
            type: 'error',
            message: safeErrorMessage(bundleErr, `Event "${ev.eventName}" could not be created.`),
          });
          continue;
        }

        await settleOrderNumber(pendingHold, true);
        pendingHold = null;
        createdEventIds.push(newEvent.id);
        createdCodes.push(code);

        // 4. Assign per-day BEO numbers to event_days
        if (ev.beoNumbers.length > 0) {
          try {
            const { data: days } = await fetchEventDays(newEvent.id, { supabase });
            if (days?.length) {
              for (const bn of ev.beoNumbers) {
                const matchDay = days.find((d) => d.event_day === bn.date);
                if (matchDay) {
                  await updateEventDay(newEvent.id, matchDay, { beo_no: bn.beoNo }, { supabase });
                }
              }
            }
          } catch (_dayErr) {
            console.warn('[BEOImportReview] per-day BEO assignment error', _dayErr);
          }
        }

        // 5. Patch additional event fields
        const patch: Record<string, any> = { tags: ['beo-import'] };
        if (ev.beoNumbers.length > 0) patch.beo_no = ev.beoNumbers[0].beoNo;
        if (ev.notes.trim()) patch.notes = ev.notes.trim();
        if (ev.attendees) patch.anticipated_attendees = Number(ev.attendees) || null;
        if (ev.clientName) patch.client_business = ev.clientName;
        if (ev.startTime) patch.event_start_time = ev.startTime;
        if (ev.endTime) patch.event_end_time = ev.endTime;
        if (parsedEvent?.load_in_date) patch.load_in_date = parsedEvent.load_in_date;
        if (parsedEvent?.load_in_time) patch.load_in_time = parsedEvent.load_in_time;
        if (parsedEvent?.setup_date) patch.setup_date = parsedEvent.setup_date;
        if (parsedEvent?.setup_time) patch.setup_time = parsedEvent.setup_time;
        if (parsedEvent?.strike_date) patch.strike_date = parsedEvent.strike_date;
        if (parsedEvent?.strike_time) patch.strike_time = parsedEvent.strike_time;
        if (packet.venue_address) {
          if (packet.venue_address.line1) patch.address_line1 = packet.venue_address.line1;
          if (packet.venue_address.line2) patch.address_line2 = packet.venue_address.line2;
          if (packet.venue_address.city) patch.city = packet.venue_address.city;
          if (packet.venue_address.state) patch.state = packet.venue_address.state;
          if (packet.venue_address.postal_code) patch.postal_code = packet.venue_address.postal_code;
          if (packet.venue_address.country) patch.country = packet.venue_address.country;
        }
        if (Object.keys(patch).length) {
          const { error: patchError } = await supabase.from('events').update(patch).eq('id', newEvent.id);
          if (patchError) {
            // The event exists and keeps its number; only these details are missing.
            toasts.push({
              type: 'warn',
              message: toPartialSaveError(patchError, `Event "${ev.eventName}" was created, but its BEO details (tags, notes, times, attendees, address) were not saved.`).message,
            });
          }
        }

        // 6. Attach client to event
        if (clientId) {
          await attachClientToEvent({ eventId: newEvent.id, clientId, business: ev.clientName, importContacts: false, supabase });

          // 6b. Branch matching: if BEO has a venue address, ensure the client has a matching branch
          if (packet.venue_address?.city || packet.venue_address?.line1) {
            try {
              const { data: branchData } = await fetchClientBranchDetails(clientId, { supabase });
              const branches = branchData?.branches || [];
              const venueAddr = packet.venue_address;
              const vCity = (venueAddr?.city || '').toLowerCase().trim();
              const vState = (venueAddr?.state || '').toLowerCase().trim();
              const vLine1 = (venueAddr?.line1 || '').toLowerCase().trim();
              // Check if any existing branch matches the venue address
              const matchingBranch = branches.find((b) => {
                const bCity = (b.billing_city || '').toLowerCase().trim();
                const bState = (b.billing_state || '').toLowerCase().trim();
                const bLine1 = (b.billing_address_line1 || '').toLowerCase().trim();
                if (vLine1 && bLine1 && vLine1 === bLine1) return true; // exact address match
                if (vCity && bCity && vCity === bCity && vState && bState && vState === bState) return true; // city+state match
                return false;
              });
              if (!matchingBranch) {
                // Create a new branch with the venue address
                const branchName = packet.venue_name || `${venueAddr?.city || ''} ${venueAddr?.state || ''}`.trim() || 'BEO Import';
                const { data: newBranch } = await insertClientBranch({ clientId, branchName, supabase });
                if (newBranch) {
                  await updateClientBranch({
                    branchId: newBranch.id,
                    patch: {
                      billing_address_line1: venueAddr?.line1 || null,
                      billing_address_line2: venueAddr?.line2 || null,
                      billing_city: venueAddr?.city || null,
                      billing_state: venueAddr?.state || null,
                      billing_postal_code: venueAddr?.postal_code || null,
                      billing_country: venueAddr?.country || null,
                    } as any,
                    supabase,
                  });
                }
              }
            } catch (_branchErr) {
              // Non-critical — don't block event creation if branch matching fails
              console.warn('[BEOImportReview] branch matching error', _branchErr);
            }
          }
        }

        // 7. Create contacts
        const selectedContacts = ev.contacts.filter((c) => c.selected && c.name);
        for (let ci = 0; ci < selectedContacts.length; ci++) {
          const c = selectedContacts[ci];
          const { first, last } = splitName(c.name);
          await createEventContact({
            eventId: newEvent.id,
            role: mapContactRole(c.role),
            position: ci,
            initial: {
              first_name: first || undefined,
              last_name: last || undefined,
              title: c.role || undefined,
              email: c.email || undefined,
              phone: c.phone || undefined,
            },
            supabase,
          });
          // Also create client_contact if client was matched/created and this is a new contact
          if (clientId) {
            await insertClientContact({
              clientId,
              values: {
                first_name: first || null,
                last_name: last || null,
                title: c.role || null,
                email: c.email || null,
                phone: c.phone || null,
              },
              supabase,
            });
          }
        }

        // 8. Create line items (with product/item match if available)
        const selectedLines = ev.lineItems.filter((li) => li.selected && li.description);
        for (let li = 0; li < selectedLines.length; li++) {
          const line = selectedLines[li];
          const effectiveProductId = line.override_product_id ?? line.matched_product_id ?? null;
          const effectiveItemId = line.override_item_id ?? line.matched_item_id ?? null;
          await upsertEventLine({
            eventId: newEvent.id,
            description: line.description,
            qty: line.quantity ?? 1,
            unitPrice: line.unit_price ?? 0,
            usageRoomDate: ev.startDate || null,
            roomKey: 'GENERAL',
            action: 'rent',
            lineNumber: li + 1,
            note: line.notes || undefined,
            productId: effectiveProductId || undefined,
            itemId: effectiveItemId || undefined,
            supabase,
          });
        }
      }

      if (createdEventIds.length) {
        const plural = createdEventIds.length > 1 ? 's' : '';
        toasts.push({ type: 'success', message: `Created ${createdEventIds.length} event${plural} from BEO. Order number${plural}: ${createdCodes.join(', ')}` });
        if (onComplete) onComplete();
        navigate(`/events/${createdEventIds[0]}`);
      }
    } catch (e: any) {
      console.error('[BEOImportReview] create error', e);
      toasts.push({ type: 'error', message: e?.message || 'Failed to create events' });
    } finally {
      // An exception before the event existed must not strand its number.
      if (pendingHold) await settleOrderNumber(pendingHold, false);
      creatingRef.current = false;
      setCreating(false);
    }
  }

  /* ── render helpers ──────────────────────────────────────────── */

  function toggleEventSelected(idx: number) {
    updateEvent(idx, { selected: !events[idx].selected });
  }
  function toggleEventExpanded(idx: number) {
    updateEvent(idx, { expanded: !events[idx].expanded });
  }
  function toggleRoom(eventIdx: number, roomIdx: number) {
    setEvents((prev) =>
      prev.map((e, ei) =>
        ei === eventIdx
          ? { ...e, rooms: e.rooms.map((r, ri) => (ri === roomIdx ? { ...r, selected: !r.selected } : r)) }
          : e,
      ),
    );
  }
  function toggleContact(eventIdx: number, contactIdx: number) {
    setEvents((prev) =>
      prev.map((e, ei) =>
        ei === eventIdx
          ? { ...e, contacts: e.contacts.map((c, ci) => (ci === contactIdx ? { ...c, selected: !c.selected } : c)) }
          : e,
      ),
    );
  }
  function toggleLineItem(eventIdx: number, liIdx: number) {
    setEvents((prev) =>
      prev.map((e, ei) =>
        ei === eventIdx
          ? { ...e, lineItems: e.lineItems.map((li, i) => (i === liIdx ? { ...li, selected: !li.selected } : li)) }
          : e,
      ),
    );
  }
  function updateLineItemMatch(eventIdx: number, liIdx: number, match: { productId: string | null; productName: string | null; itemId: string | null }) {
    setEvents((prev) =>
      prev.map((e, ei) =>
        ei === eventIdx
          ? { ...e, lineItems: e.lineItems.map((li, i) => (i === liIdx ? { ...li, override_product_id: match.productId, override_product_name: match.productName, override_item_id: match.itemId } : li)) }
          : e,
      ),
    );
  }
  function clearLineItemMatch(eventIdx: number, liIdx: number) {
    updateLineItemMatch(eventIdx, liIdx, { productId: null, productName: null, itemId: null });
  }

  /* ── inline product search for line item override ─────────── */
  const [productSearchKey, setProductSearchKey] = React.useState<string | null>(null); // "evIdx-liIdx" or null
  const [productSearchTerm, setProductSearchTerm] = React.useState('');
  const [productSearchResults, setProductSearchResults] = React.useState<Array<{ productId: string; productName: string; itemId: string | null; itemName: string }>>([]);
  const [productSearchLoading, setProductSearchLoading] = React.useState(false);
  const productSearchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  function openProductSearch(eventIdx: number, liIdx: number) {
    const key = `${eventIdx}-${liIdx}`;
    setProductSearchKey(key);
    const li = events[eventIdx]?.lineItems[liIdx];
    setProductSearchTerm(li?.description || '');
    setProductSearchResults([]);
  }
  function closeProductSearch() {
    setProductSearchKey(null);
    setProductSearchTerm('');
    setProductSearchResults([]);
  }

  React.useEffect(() => {
    if (!productSearchKey) return;
    const trimmed = productSearchTerm.trim();
    if (trimmed.length < 2) { setProductSearchResults([]); return; }
    if (productSearchTimerRef.current) clearTimeout(productSearchTimerRef.current);
    productSearchTimerRef.current = setTimeout(async () => {
      setProductSearchLoading(true);
      try {
        const { data } = await searchProductItemLinks({
          select: 'product_id, products(product_id, name_public, default_item_id), items(item_id, name_internal)',
          searchTerm: trimmed,
          productStatus: 'active',
          limit: 10,
        });
        setProductSearchResults(
          (data || []).map((row: any) => ({
            productId: row.products?.product_id || row.product_id,
            productName: row.products?.name_public || '',
            itemId: row.products?.default_item_id || row.items?.item_id || null,
            itemName: row.items?.name_internal || '',
          })),
        );
      } catch { setProductSearchResults([]); }
      finally { setProductSearchLoading(false); }
    }, 300);
    return () => { if (productSearchTimerRef.current) clearTimeout(productSearchTimerRef.current); };
  }, [productSearchTerm, productSearchKey]);

  const selectedCount = events.filter((e) => e.selected).length;

  const fieldStyle: React.CSSProperties = { width: '100%', padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.875rem' };
  const labelStyle: React.CSSProperties = { fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' };
  const sectionHeadingStyle: React.CSSProperties = { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.04em', color: 'var(--text-muted)', margin: '1rem 0 0.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.25rem' };
  const badgeNew: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--warning-light, #fff3cd)', color: 'var(--warning-dark, #856404)', padding: '0.1rem 0.4rem', borderRadius: '4px', marginLeft: '0.35rem' };
  const badgeMatched: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--primary-light, #e3f2fd)', color: 'var(--primary, #1976d2)', padding: '0.1rem 0.4rem', borderRadius: '4px', marginLeft: '0.35rem' };
  const badgeInHouse: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--info-light, #e0f7fa)', color: 'var(--info-dark, #00695c)', padding: '0.1rem 0.4rem', borderRadius: '4px', marginLeft: '0.35rem' };
  const badgeExists: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--danger-light, #fce4ec)', color: 'var(--danger, #e53e3e)', padding: '0.1rem 0.4rem', borderRadius: '4px', marginLeft: '0.35rem' };
  const badgeGeneric: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--warning-light, #fff3cd)', color: 'var(--warning-dark, #856404)', padding: '0.1rem 0.4rem', borderRadius: '4px' };
  const badgeMatchHigh: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--success-light, #e8f5e9)', color: 'var(--success-dark, #2e7d32)', padding: '0.1rem 0.4rem', borderRadius: '4px', cursor: 'pointer' };
  const badgeMatchMedium: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--warning-light, #fff3cd)', color: 'var(--warning-dark, #856404)', padding: '0.1rem 0.4rem', borderRadius: '4px', cursor: 'pointer' };
  const badgeMatchLow: React.CSSProperties = { fontSize: '0.65rem', fontWeight: 700, background: 'var(--surface-raised, #f5f5f5)', color: 'var(--text-muted)', padding: '0.1rem 0.4rem', borderRadius: '4px', cursor: 'pointer' };

  return (
    <div className="modal" style={{ maxWidth: '780px', width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Import Events from BEO</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} aria-label="Close" disabled={creating}>&times;</button>
      </div>

      {/* Body */}
      <div className="modal-body" style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: '1 1 auto', display: 'block' }}>

        {/* ── Summary Phase: Multi-Venue Grouping ──────────────── */}
        {step === 'summary' && isMultiVenue && (() => {
          // Group events by normalized venue name
          const venueGroups = new Map<string, number[]>();
          events.forEach((ev, i) => {
            const key = (ev.venueName || '').trim().toLowerCase() || '__no_venue__';
            if (!venueGroups.has(key)) venueGroups.set(key, []);
            venueGroups.get(key)!.push(i);
          });
          return (
            <>
              <div style={{ padding: '0.6rem 0.75rem', background: 'var(--info-light, #e0f7fa)', color: 'var(--info-dark, #00695c)', border: '1px solid var(--info-border, #b2dfdb)', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                <strong>Multiple Venues Detected</strong> — Events are grouped by venue. Assign the correct location for each group, then continue to review individual events.
              </div>
              {[...venueGroups.entries()].map(([venueKey, indices]) => {
                const representative = events[indices[0]];
                const displayName = venueKey === '__no_venue__' ? '(No Venue)' : representative.venueName;
                return (
                  <div key={venueKey} style={{ border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '0.75rem', overflow: 'hidden' }}>
                    <div style={{ padding: '0.6rem 0.75rem', background: 'var(--surface-raised, #f8f8f8)', borderBottom: '1px solid var(--border)' }}>
                      <strong style={{ fontSize: '0.9rem' }}>{displayName}</strong>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {indices.length} event{indices.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ padding: '0.75rem' }}>
                      {/* Venue match */}
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={labelStyle}>Venue</div>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          {representative.venueMatches.length > 0 && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                              <input type="radio" checked={!representative.createNewVenue} onChange={() => {
                                setEvents((prev) => prev.map((e, i) => indices.includes(i) ? { ...e, createNewVenue: false } : e));
                              }} />
                              Match:
                              <select style={{ ...fieldStyle, width: 'auto' }} value={representative.venueId || ''} onChange={(e) => {
                                const val = e.target.value || null;
                                setEvents((prev) => prev.map((ev, i) => indices.includes(i) ? { ...ev, venueId: val, createNewVenue: false, locationAutoResolved: false } : ev));
                              }}>
                                <option value="">Select...</option>
                                {representative.venueMatches.map((v) => <option key={v.id} value={v.id}>{v.venue_name}{v.city ? ` (${v.city}, ${v.state || ''})` : ''}</option>)}
                              </select>
                            </label>
                          )}
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem' }}>
                            <input type="radio" checked={representative.createNewVenue} onChange={() => {
                              setEvents((prev) => prev.map((e, i) => indices.includes(i) ? { ...e, createNewVenue: true, venueId: null } : e));
                            }} />
                            Create new{displayName ? `: "${displayName}"` : ''}
                          </label>
                        </div>
                      </div>
                      {/* Location */}
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={labelStyle}>Location *</div>
                        <select style={fieldStyle} value={representative.siteLocationId || ''} onChange={(e) => {
                          const val = e.target.value || null;
                          setEvents((prev) => prev.map((ev, i) => indices.includes(i) ? { ...ev, siteLocationId: val, locationAutoResolved: false } : ev));
                        }}>
                          <option value="">Select location...</option>
                          {sites.map((s) => <option key={s.location_id} value={s.location_id}>{s.name}</option>)}
                        </select>
                      </div>
                      {/* Event list */}
                      <div style={{ marginTop: '0.5rem' }}>
                        {indices.map((idx) => {
                          const ev = events[idx];
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', fontSize: '0.85rem', borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
                              <input type="checkbox" checked={ev.selected} onChange={() => toggleEventSelected(idx)} />
                              <span style={{ flex: 1 }}>{ev.eventName || `Event ${idx + 1}`}</span>
                              {ev.startDate && <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{fmtDate(ev.startDate)}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-primary" onClick={() => setStep('review')}>
                  Continue to Review &rarr;
                </button>
              </div>
            </>
          );
        })()}

        {/* ── Review Phase ───────────────────────────────────────── */}
        {step === 'review' && (
          <>
            {isMultiVenue && (
              <div style={{ marginBottom: '0.75rem' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStep('summary')}>
                  &larr; Back to Venue Summary
                </button>
              </div>
            )}

        {/* ── Truncation warning ────────────────────────────────── */}
        {packet.truncated && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'var(--warning-bg, #fff3cd)', color: 'var(--warning-text, #856404)', border: '1px solid var(--warning-border, #ffc107)', borderRadius: '6px', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
            <strong>Note:</strong> Only {packet.truncated.pages_processed} of {packet.truncated.pages_total} pages were processed. Some events at the end of the document may not appear here.
          </div>
        )}

        {/* ── Events Accordion ──────────────────────────────────── */}
        <div style={sectionHeadingStyle}>Events ({selectedCount}/{events.length} selected)</div>

        {events.map((ev, evIdx) => (
          <div key={evIdx} style={{ border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '0.75rem', overflow: 'hidden', opacity: ev.selected ? 1 : 0.6, flexShrink: 0 }}>
            {/* Accordion header */}
            <div
              style={{ padding: '0.6rem 0.75rem', background: 'var(--surface-raised, #f8f8f8)', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => toggleEventExpanded(evIdx)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  aria-label={`Include ${ev.eventName || `Event ${evIdx + 1}`}`}
                  checked={ev.selected}
                  onChange={(e) => { e.stopPropagation(); toggleEventSelected(evIdx); }}
                  onClick={(e) => e.stopPropagation()}
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{ev.expanded ? '\u25BC' : '\u25B6'}</span>
                <strong style={{ fontSize: '0.9rem', flex: 1 }}>
                  {ev.eventName || `Event ${evIdx + 1}`}
                  {ev.clientName && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>— {ev.clientName}</span>}
                </strong>
                {ev.existingEventId && <span style={badgeExists}>ALREADY EXISTS</span>}
                {packet.events[evIdx]?.is_in_house && <span style={badgeInHouse}>IN-HOUSE</span>}
                {ev.clientMatch && <span style={badgeMatched}>Client Matched</span>}
                {ev.clientIsNew && <span style={badgeNew}>NEW Client</span>}
              </div>
              {!ev.expanded && (
                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem', marginLeft: '2.75rem', fontSize: '0.75rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                  {(ev.startDate || ev.endDate) && <span>{fmtDateRange(ev.startDate, ev.endDate)}</span>}
                  {ev.beoNumbers.length > 0 && <span>BEO #{ev.beoNumbers.map((b) => b.beoNo).join(', #')}</span>}
                  {ev.rooms.length > 0 && <span>{ev.rooms.map((r) => r.name).join(', ')}</span>}
                </div>
              )}
            </div>

            {/* Accordion body */}
            {ev.expanded && (
              <div style={{ padding: '0.75rem', borderTop: '1px solid var(--border)' }}>
                {/* Duplicate warning */}
                {ev.existingEventId && (
                  <div style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', borderRadius: '6px', background: 'var(--danger-light, #fce4ec)', color: 'var(--danger, #e53e3e)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong>Duplicate:</strong> An event with BEO #{ev.beoNumbers.map((b) => b.beoNo).join(', #') || '?'} already exists
                    {ev.existingEventName && <span>(&ldquo;{ev.existingEventName}&rdquo;)</span>}.
                    <a href={`/events/${ev.existingEventId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', marginLeft: 'auto', whiteSpace: 'nowrap' }}>View existing</a>
                  </div>
                )}

                {/* Per-event venue & location */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem', padding: '0.5rem', background: 'var(--surface-soft, #fafafa)', borderRadius: '6px', border: '1px solid var(--border-subtle, var(--border))' }}>
                  <div>
                    <div style={labelStyle}>Venue</div>
                    {ev.venueMatches.length > 0 ? (
                      <select style={fieldStyle} value={ev.venueId || (ev.createNewVenue ? '__new__' : '')} onChange={(e) => {
                        const val = e.target.value;
                        if (val === '__new__') {
                          updateEvent(evIdx, { createNewVenue: true, venueId: null });
                        } else {
                          updateEvent(evIdx, { venueId: val || null, createNewVenue: false, locationAutoResolved: false });
                        }
                      }}>
                        <option value="">Select...</option>
                        {ev.venueMatches.map((v) => <option key={v.id} value={v.id}>{v.venue_name}{v.city ? ` (${v.city}, ${v.state || ''})` : ''}</option>)}
                        <option value="__new__">+ Create new: {ev.venueName}</option>
                      </select>
                    ) : (
                      <div style={{ ...fieldStyle, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={badgeNew}>NEW</span> {ev.venueName || '(none)'}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={labelStyle}>Location *</div>
                    <select style={fieldStyle} aria-label={`Location for ${ev.eventName || `Event ${evIdx + 1}`}`} value={ev.siteLocationId || ''} onChange={(e) => updateEvent(evIdx, { siteLocationId: e.target.value || null, locationAutoResolved: false })}>
                      <option value="">Select location...</option>
                      {sites.map((s) => <option key={s.location_id} value={s.location_id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Event details */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={labelStyle}>Event Name *</div>
                    <input style={fieldStyle} value={ev.eventName} onChange={(e) => updateEvent(evIdx, { eventName: e.target.value })} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', position: 'relative' }}>
                    <div style={labelStyle}>Client / Organization</div>
                    {ev.clientMatch && !ev.clientSearchOpen ? (
                      <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ flex: 1 }}>{ev.clientMatch.businessName}</span>
                        <span style={badgeMatched}>Matched</span>
                        <button type="button" onClick={() => clearClientMatch(evIdx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '0 0.25rem' }} title="Change client">&times;</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <input
                            style={{ ...fieldStyle, flex: 1 }}
                            value={ev.clientSearchOpen ? ev.clientSearchQuery : ev.clientName}
                            onChange={(e) => {
                              if (ev.clientSearchOpen) {
                                handleClientSearchChange(evIdx, e.target.value);
                              } else {
                                updateEvent(evIdx, { clientName: e.target.value });
                              }
                            }}
                            onFocus={() => {
                              if (!ev.clientSearchOpen) {
                                updateEvent(evIdx, { clientSearchOpen: true, clientSearchQuery: ev.clientName });
                                handleClientSearchChange(evIdx, ev.clientName);
                              }
                            }}
                            placeholder="Search clients..."
                          />
                          {ev.clientIsNew && !ev.clientMatch && <span style={badgeNew}>NEW</span>}
                          {ev.clientSearchOpen && (
                            <button type="button" onClick={() => updateEvent(evIdx, { clientSearchOpen: false, clientSearchQuery: '', clientSearchResults: [] })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '0 0.25rem' }} title="Cancel search">&times;</button>
                          )}
                        </div>
                        {ev.clientSearchOpen && (ev.clientSearchResults.length > 0 || ev.clientSearchLoading) && (
                          <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 10, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                            {ev.clientSearchLoading && <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Searching...</div>}
                            {ev.clientSearchResults.map((r) => (
                              <div
                                key={r.id}
                                style={{ padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid var(--border)' }}
                                onMouseDown={(e) => { e.preventDefault(); selectClient(evIdx, r); }}
                              >
                                {r.businessName}
                              </div>
                            ))}
                            {!ev.clientSearchLoading && ev.clientSearchResults.length === 0 && ev.clientSearchQuery.trim() && (
                              <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                No matches — will create as new client
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <div style={labelStyle}>BEO #{ev.beoNumbers.length > 1 ? 's' : ''}</div>
                    {ev.beoNumbers.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        {ev.beoNumbers.map((b, bi) => (
                          <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                            <span style={{ color: 'var(--text-muted)', minWidth: '5rem' }}>{b.date ? new Date(b.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : '—'}</span>
                            <span style={{ fontWeight: 600 }}>#{b.beoNo}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ ...fieldStyle, color: 'var(--text-muted)' }}>None</div>
                    )}
                  </div>
                  <div>
                    <div style={labelStyle}>R2 Order #</div>
                    <div style={{ ...fieldStyle, background: 'var(--surface-raised, #f5f5f5)', cursor: 'default', color: 'var(--text-muted)' }}>
                      {ev.siteLocationId ? 'Assigned when created' : 'Select a location'}
                    </div>
                  </div>
                  <div>
                    <div style={labelStyle}>Start Date</div>
                    <input type="date" style={fieldStyle} value={ev.startDate} onChange={(e) => updateEvent(evIdx, { startDate: e.target.value })} />
                  </div>
                  <div>
                    <div style={labelStyle}>Start Time</div>
                    <input type="time" style={fieldStyle} value={ev.startTime ? ev.startTime.slice(0, 5) : ''} onChange={(e) => updateEvent(evIdx, { startTime: e.target.value ? e.target.value + ':00' : '' })} />
                  </div>
                  <div>
                    <div style={labelStyle}>End Date</div>
                    <input type="date" style={fieldStyle} value={ev.endDate} onChange={(e) => updateEvent(evIdx, { endDate: e.target.value })} />
                  </div>
                  <div>
                    <div style={labelStyle}>End Time</div>
                    <input type="time" style={fieldStyle} value={ev.endTime ? ev.endTime.slice(0, 5) : ''} onChange={(e) => updateEvent(evIdx, { endTime: e.target.value ? e.target.value + ':00' : '' })} />
                  </div>
                  <div>
                    <div style={labelStyle}>Expected Attendees</div>
                    <input type="number" style={fieldStyle} value={ev.attendees} onChange={(e) => updateEvent(evIdx, { attendees: e.target.value })} />
                  </div>
                </div>

                {/* Rooms */}
                {ev.rooms.length > 0 && (
                  <>
                    <div style={{ ...sectionHeadingStyle, marginTop: '0.75rem' }}>Rooms ({ev.rooms.filter((r) => r.selected).length}/{ev.rooms.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {ev.rooms.map((r, ri) => (
                        <label key={ri} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={r.selected} onChange={() => toggleRoom(evIdx, ri)} />
                          <strong>{r.name}</strong>
                          {r.setup_style && <span style={{ color: 'var(--text-muted)' }}>({r.setup_style})</span>}
                          {r.capacity != null && <span style={{ color: 'var(--text-muted)' }}>cap: {r.capacity}</span>}
                          {r.templateId && <span style={badgeMatched}>matched</span>}
                          {r.isNew && !r.templateId && <span style={badgeNew}>NEW</span>}
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {/* Contacts */}
                {ev.contacts.length > 0 && (
                  <>
                    <div style={{ ...sectionHeadingStyle, marginTop: '0.75rem' }}>Contacts ({ev.contacts.filter((c) => c.selected).length}/{ev.contacts.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {ev.contacts.map((c, ci) => (
                        <label key={ci} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={c.selected} onChange={() => toggleContact(evIdx, ci)} />
                          <strong>{c.name || '(unnamed)'}</strong>
                          {c.role && <span style={{ color: 'var(--text-muted)' }}>- {c.role}</span>}
                          {c.email && <span style={{ color: 'var(--text-muted)' }}>{c.email}</span>}
                          {c.phone && <span style={{ color: 'var(--text-muted)' }}>{c.phone}</span>}
                          <span style={{ fontSize: '0.7rem', background: 'var(--surface-raised, #f0f0f0)', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>{mapContactRole(c.role)}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {/* Line Items */}
                {ev.lineItems.length > 0 ? (
                  <>
                    <div style={{ ...sectionHeadingStyle, marginTop: '0.75rem' }}>Line Items ({ev.lineItems.filter((li) => li.selected).length}/{ev.lineItems.length})</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                            <th style={{ padding: '0.35rem 0.5rem', width: '2rem' }}></th>
                            <th style={{ padding: '0.35rem 0.5rem' }}>Description</th>
                            <th style={{ padding: '0.35rem 0.5rem' }}>Matched Product</th>
                            <th style={{ padding: '0.35rem 0.5rem', width: '4rem', textAlign: 'right' }}>Qty</th>
                            <th style={{ padding: '0.35rem 0.5rem', width: '5rem', textAlign: 'right' }}>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ev.lineItems.map((li, liIdx) => {
                            const displayProductName = li.override_product_name ?? li.matched_product_name ?? null;
                            const displayProductId = li.override_product_id ?? li.matched_product_id ?? null;
                            const confidence = li.override_product_id ? 'high' : (li.match_confidence || 'none');
                            const isSearchOpen = productSearchKey === `${evIdx}-${liIdx}`;
                            return (
                              <React.Fragment key={liIdx}>
                                <tr style={{ borderBottom: '1px solid var(--border)', opacity: li.selected ? 1 : 0.5 }}>
                                  <td style={{ padding: '0.35rem 0.5rem' }}>
                                    <input type="checkbox" checked={li.selected} onChange={() => toggleLineItem(evIdx, liIdx)} />
                                  </td>
                                  <td style={{ padding: '0.35rem 0.5rem' }}>
                                    {li.description}
                                    {li.notes && <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>({li.notes})</span>}
                                    {li.is_generic && <span style={{ ...badgeGeneric, marginLeft: '0.4rem' }} title="This looks like a summary line, not specific equipment">generic</span>}
                                  </td>
                                  <td style={{ padding: '0.35rem 0.5rem' }}>
                                    {displayProductId && displayProductName ? (
                                      <span
                                        style={confidence === 'high' ? badgeMatchHigh : confidence === 'medium' ? badgeMatchMedium : badgeMatchLow}
                                        onClick={() => openProductSearch(evIdx, liIdx)}
                                        title={`${displayProductName} (${confidence} confidence) — click to change`}
                                      >
                                        {displayProductName}
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', cursor: 'pointer', background: 'var(--surface-raised, #f5f5f5)', border: '1px solid var(--border)', borderRadius: '4px', color: 'var(--text-muted)' }}
                                        onClick={() => openProductSearch(evIdx, liIdx)}
                                      >
                                        Search...
                                      </button>
                                    )}
                                    {displayProductId && (
                                      <button
                                        type="button"
                                        style={{ marginLeft: '0.25rem', fontSize: '0.65rem', padding: '0 0.25rem', cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-muted)' }}
                                        onClick={() => clearLineItemMatch(evIdx, liIdx)}
                                        title="Clear match"
                                      >&times;</button>
                                    )}
                                  </td>
                                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{li.quantity ?? 1}</td>
                                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{li.unit_price != null ? `$${li.unit_price.toFixed(2)}` : '-'}</td>
                                </tr>
                                {isSearchOpen && (
                                  <tr>
                                    <td colSpan={5} style={{ padding: '0.35rem 0.5rem', background: 'var(--surface-raised, #fafafa)' }}>
                                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.3rem' }}>
                                        <input
                                          type="text"
                                          value={productSearchTerm}
                                          onChange={(e) => setProductSearchTerm(e.target.value)}
                                          placeholder="Search products..."
                                          style={{ ...fieldStyle, flex: 1, fontSize: '0.8rem', padding: '0.25rem 0.4rem' }}
                                          autoFocus
                                        />
                                        <button type="button" style={{ fontSize: '0.75rem', cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-muted)' }} onClick={closeProductSearch}>Cancel</button>
                                      </div>
                                      {productSearchLoading && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Searching...</div>}
                                      {!productSearchLoading && productSearchResults.length > 0 && (
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '4px' }}>
                                          {productSearchResults.map((r, rIdx) => (
                                            <div
                                              key={rIdx}
                                              style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem', cursor: 'pointer', borderBottom: rIdx < productSearchResults.length - 1 ? '1px solid var(--border)' : 'none' }}
                                              onMouseOver={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--primary-light, #e3f2fd)'; }}
                                              onMouseOut={(e) => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                                              onClick={() => {
                                                updateLineItemMatch(evIdx, liIdx, { productId: r.productId, productName: r.productName, itemId: r.itemId });
                                                closeProductSearch();
                                              }}
                                            >
                                              <strong>{r.productName}</strong>
                                              {r.itemName && <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem' }}>{r.itemName}</span>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {!productSearchLoading && productSearchTerm.trim().length >= 2 && productSearchResults.length === 0 && (
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No products found</div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div style={{ ...sectionHeadingStyle, marginTop: '0.75rem' }}>Line Items</div>
                )}
                {ev.lineItems.length === 0 && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No line items found in this event.</div>
                )}

                {/* Notes */}
                <div style={{ ...sectionHeadingStyle, marginTop: '0.75rem' }}>Notes</div>
                <textarea
                  style={{ ...fieldStyle, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                  value={ev.notes}
                  onChange={(e) => updateEvent(evIdx, { notes: e.target.value })}
                  placeholder="AV requirements, catering, special notes..."
                />
              </div>
            )}
          </div>
        ))}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="modal-actions" style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
        <button type="button" className="btn" onClick={onCancel} disabled={creating}>Cancel</button>
        {step === 'review' && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={creating || !selectedCount}
          >
            {creating ? 'Creating...' : `Create ${selectedCount} Event${selectedCount !== 1 ? 's' : ''}`}
          </button>
        )}
      </div>
    </div>
  );
}
